import fetchAPI from '../fetchAPI';

/**
 * Signing a company Admin in to the dashboard (`/admin`). Every way in ends with
 * a one-time code that `callExchangeDashboardCode` swaps for a Firebase custom
 * token (server: `dashboardSessionRoutes.ts`):
 * - the Negotiator extension hands one over (its "Open dashboard" button, or
 *   automatically when `/admin/login` asks it — see `requestCodeFromExtension`);
 * - a CRM sign-in (`/login/<crm>?return=dashboard`) or a magic link with
 *   `target: 'dashboard'` comes back to `/admin/sso?code=…`.
 */
export type LoginMode = 'crm-redirect' | 'magic-link' | 'no-account';

export const callExchangeDashboardCode = (
  code: string
): Promise<iResponse<{ token: string; email: string; message?: string }>> =>
  fetchAPI({
    method: 'POST',
    route: 'dashboard-session/exchange',
    data: { code } as unknown as APIData
  });

/** Which sign-in this email uses — the same answer the extension gets. */
export const callGetLoginMethod = (
  email: string
): Promise<iResponse<{ mode: LoginMode; crm?: string }>> =>
  fetchAPI({
    method: 'GET',
    route: 'login/method',
    queryString: `email=${encodeURIComponent(email)}`
  });

/** Email a sign-in link that lands on the dashboard. The reply is the same whether or not the address exists. */
export const callRequestDashboardMagicLink = (
  email: string
): Promise<iResponse<{ requestId: string; message: string }>> =>
  fetchAPI({
    method: 'POST',
    route: 'login/magic-link',
    data: { email, target: 'dashboard' } as unknown as APIData
  });

// The extension's slugs (negotiator `config/crmRedirect.ts`): a CRM it can't
// complete falls back to Reapit, as the extension does.
const CRM_SLUGS: Record<string, string> = { reapit: 'reapit', ghl: 'ghl' };

/** Where "Continue with <CRM>" sends the browser: the CRM's sign-in, coming back to the dashboard. */
export function crmSignInUrl(crm?: string): string {
  const slug = CRM_SLUGS[(crm ?? '').trim().toLowerCase()] ?? 'reapit';
  return `${process.env.REACT_APP_API_BASE}/login/${slug}?return=dashboard`;
}

/**
 * Ask the Negotiator extension, if it's installed and signed in, for a code.
 * Its content script on this page answers on `window.postMessage`: first an
 * acknowledgement straight away, then the code (or null — signed out, or not an
 * Admin). No acknowledgement within `ackMs` means no extension, and the login
 * form is shown; after one, the code gets `codeMs`.
 */
export function requestCodeFromExtension(ackMs = 1000, codeMs = 8000): Promise<string | null> {
  return new Promise((resolve) => {
    const requestId = Math.random().toString(36).slice(2);
    let timer: ReturnType<typeof setTimeout>;
    const finish = (code: string | null) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(code);
    };
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      const message = event.data;
      if (message?.source !== 'salesrook-extension' || message?.requestId !== requestId) return;
      if (message.type === 'dashboard-sign-in-ack') {
        clearTimeout(timer);
        timer = setTimeout(() => finish(null), codeMs);
        return;
      }
      if (message.type === 'dashboard-sign-in-code') {
        finish(typeof message.code === 'string' ? message.code : null);
      }
    };
    timer = setTimeout(() => finish(null), ackMs);
    window.addEventListener('message', onMessage);
    window.postMessage(
      { source: 'salesrook-dashboard', type: 'request-dashboard-sign-in-code', requestId },
      window.location.origin
    );
  });
}
